import React, {
  useState,
  useEffect,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { Link } from 'react-router-dom';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Tooltip,
  List,
  ListItem,
  Button,
  CircularProgress,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Divider,
  ListItemSecondaryAction,
  Typography,
} from '@material-ui/core';
import ArrowBack from '@material-ui/icons/KeyboardArrowLeft';
import ArrowForward from '@material-ui/icons/KeyboardArrowRight';
import {
  getEventTemplates,
  searchEventTemplates,
} from '@pingum/app-state-management/eventTemplate/actions';

import { getTenantFromUrl } from '../../../../../utils/urlHelper';
import EmptyList from '../../../EmptyList';
import StatusBadgeList from '../../../StatusBadgeList';
import TagList from '../../../TagList';
import { isUserAdmin } from '../../../../../utils/tenantAuthenticationHelper';

import styles from './styles';

const START_PAGINATION = {
  page: 0,
  size: 25,
  search: '',
  sort: ['name,asc'],
};

const getPaginationInfo = (eventTemplates) => {
  const startingNumber = eventTemplates.number * eventTemplates.size;
  const endingNumber = startingNumber + eventTemplates.numberOfElements;
  return `showing ${startingNumber}-${endingNumber} of ${eventTemplates.totalElements}`;
};

const useStyles = makeStyles(styles);
const profileUserTenants = 'https://pingum.io/tenants';

export const EventTemplateSearchListComponent = ({
  onSelectEventTemplate,
  statuses,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  // local state
  const [dashboardSearch, setDashboardSearch] = useState(true);
  const [pagination, setPagination] = useState(START_PAGINATION);

  const { user: profile } = useSelector((state) => state.user);
  const { currentFilterDashboard } = useSelector((state) => state.dashboard);
  const {
    eventTemplatesPageable,
    eventTemplates,
  } = useSelector((state) => state.eventTemplate);
  const { isFetchingEventTemplates } = useSelector((state) => state.eventTemplate);
  const userRoles = profile[profileUserTenants][getTenantFromUrl()].roles;
  const isAdmin = isUserAdmin(userRoles);

  const searchDashboardTemplates = dashboardSearch && currentFilterDashboard.id >= 0;

  useEffect(() => {
    // on dashboard or search change
    if (searchDashboardTemplates) {
      // a dashboard is selected so get filtered list
      dispatch(searchEventTemplates(false, {
        tagConditions: currentFilterDashboard.dashboardData.tagConditions,
        statusIds: currentFilterDashboard.dashboardData.statusIds,
        search: currentFilterDashboard.dashboardData.searchText,
      }));
    } else {
      // no dashboard selected so get paginated list of workflows
      dispatch(getEventTemplates(false, START_PAGINATION));
      setPagination(START_PAGINATION);
    }
  }, [dashboardSearch, currentFilterDashboard.id]);

  const handleChangePage = (isIncrement) => {
    const paginationCopy = pagination;
    paginationCopy.page = isIncrement
      ? pagination.page + 1
      : pagination.page - 1;
    dispatch(getEventTemplates(false, paginationCopy));
  };

  return (
    <div className={classes.eventTemplateSearchListContainer}>
      <Typography variant="body1">
        Select a workflow or
        <Button size="small" variant="text" name="start-without-workflow-button" onClick={() => { onSelectEventTemplate(); }}>
          start without a workflow
        </Button>
      </Typography>
      <div>
        <Tooltip position="top" title="Only show workflows that match with the current selected dashboard and filter">
          <FormControlLabel
            control={(
              <Checkbox
                checked={dashboardSearch}
                onChange={() => { setDashboardSearch(!dashboardSearch); }}
                value={dashboardSearch}
                color="primary"
              />
            )}
            label={<small>Filter workflows for dashboard</small>}
          />
        </Tooltip>
      </div>
      <div className={classes.contentContainer}>
        {
          isFetchingEventTemplates
            && (
              <div className={classes.centerProgress}>
                <CircularProgress />
              </div>
            )
        }
        {
          !isFetchingEventTemplates
            && (
              searchDashboardTemplates
                ? eventTemplates.length === 0
                : eventTemplatesPageable.content.length === 0
            )
            && (
              <EmptyList
                title="No workflows found."
                description={
                  isAdmin
                    ? (
                      <div>
                        <div>
                          You can manage your workflows from the
                          &nbsp;
                          <Link
                            to="/settings/workflows"
                            href="/settings/workflows"
                          >
                            Event Settings
                          </Link> page.
                        </div>
                      </div>
                    )
                    : <div>Contact an organization admin to request a new workflow.</div>
                }
                icon="info"
              />
            )
          }
        <List name="event-template-search-list">
          {
            sortBy((searchDashboardTemplates
              ? eventTemplates
              : eventTemplatesPageable.content), ['name']).map((template) => (
                <>
                  <ListItem
                    key={`event-template-${template.id}`}
                    button
                    className={classes.eventTemplateListItem}
                    onClick={() => { onSelectEventTemplate(template); }}
                  >
                    <ListItemText
                      classes={{
                        primary: classes.listItemPrimary,
                        root: classes.listItemRoot,
                      }}
                      primary={template.name}
                      secondary={(template.tags && template.tags.length > 0)
                        ? <TagList tags={template.tags} size="small" />
                        : undefined}
                    />
                    <ListItemSecondaryAction>
                      <StatusBadgeList
                        statuses={statuses}
                        displayedStatuses={template.workflowStatuses}
                        maxLength={4}
                        statusIdField="statusId"
                        hideLabel
                        mini
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider variant="middle" component="li" />
                </>
            ))
          }
          {
            !isFetchingEventTemplates
              && !searchDashboardTemplates
              && (
                <div className={classes.paginationContainer}>
                  <Button className={classes.paginationButton} size="small" disabled={eventTemplatesPageable.number === 0} onClick={() => { handleChangePage(false); }}>
                    <ArrowBack />
                    Back
                  </Button>
                  <Button className={classes.paginationButton} size="small" onClick={() => { handleChangePage(true); }} disabled={eventTemplatesPageable.totalPages === (eventTemplatesPageable.number + 1)}>
                    Next
                    <ArrowForward />
                  </Button>
                  <div className={classes.alignCenter}>
                    {getPaginationInfo(eventTemplatesPageable)}
                  </div>
                </div>
              )
          }
        </List>
      </div>
    </div>
  );
};

EventTemplateSearchListComponent.propTypes = {
  onSelectEventTemplate: PropTypes.func,
  statuses: PropTypes.array,
};
EventTemplateSearchListComponent.defaultProps = {
  onSelectEventTemplate: () => {},
  statuses: [],
};

export default EventTemplateSearchListComponent;
